import React from "react";

/**
 * Time Machine #114 Phase 4.5 — full-day time slider with event ticks.
 *
 * The slider's range is [00:00 UTC, end-of-day UTC] of the picked day. Each
 * tick on the slider corresponds to a unique event timestamp from the merged
 * Attributes + ActiveQueue events list (Candidates excluded — would crowd the
 * slider; their events are still applied to state, just not surfaced as ticks).
 *
 * Interactions:
 *   - Drag thumb: on release, snap to nearest tick (or to the bound if no
 *     ticks). Calls onChange with the snapped Date.
 *   - "‹" / "›" arrows on either side: jump to prev/next event. If at the
 *     edges, button is disabled.
 *
 * Props:
 *   day            — Date (local midnight of the picked day, as set by
 *                    AppContext.enterTimeTravel). Used as the anchor for the
 *                    slider's 24h range: startMs = day.getTime(),
 *                    endMs = startMs + 24h - 1ms. Display via formatHHMM uses
 *                    LOCAL hours/minutes, so the range visually spans
 *                    [00:00, 23:59] in the user's wall-clock time.
 *   value          — Date (current slider position). Must be within day.
 *   events         — Array of {timestamp, ...} sorted ascending. Used for
 *                    ticks AND prev/next-event jumps.
 *   onChange       — fn(Date). Fires on slider release (snapped) and on
 *                    arrow-button click.
 *   disabled       — bool. Disables interactions while bundles are loading.
 */
const TimeSlider = ({ day, value, events, onChange, disabled }) => {
  const dayValid = day instanceof Date && !isNaN(day.getTime());
  const valueValid = value instanceof Date && !isNaN(value.getTime());

  // Day bounds in absolute ms. `day` is a LOCAL-midnight Date set by
  // AppContext.enterTimeTravel; .getTime() is the absolute UTC ms of that
  // moment. The 24h window from there matches the user's local calendar day,
  // so formatHHMM (LOCAL getHours/getMinutes) renders 00:00 → 23:59
  // monotonically across the slider — no UTC/local wrap-around.
  const startMs = dayValid ? day.getTime() : 0;
  const endMs = dayValid ? startMs + 24 * 60 * 60 * 1000 - 1 : 1;
  const valueMs = valueValid ? Math.max(startMs, Math.min(endMs, value.getTime())) : startMs;

  // Sort + dedupe event timestamps for tick rendering. (Caller usually
  // pre-sorts but we re-sort defensively.)
  const tickMs = React.useMemo(() => {
    if (!Array.isArray(events) || events.length === 0) return [];
    const set = new Set();
    for (const ev of events) {
      const ms = new Date(ev.timestamp).getTime();
      if (!isNaN(ms) && ms >= startMs && ms <= endMs) set.add(ms);
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [events, startMs, endMs]);

  // Bisect helpers for prev/next-event arrows.
  const prevEventMs = React.useMemo(() => {
    // Strictly less than current value
    let prev = null;
    for (const t of tickMs) {
      if (t < valueMs) prev = t;
      else break;
    }
    return prev;
  }, [tickMs, valueMs]);

  const nextEventMs = React.useMemo(() => {
    for (const t of tickMs) {
      if (t > valueMs) return t;
    }
    return null;
  }, [tickMs, valueMs]);

  const fireOnChange = (ms) => {
    if (typeof onChange !== "function") return;
    onChange(new Date(ms));
  };

  const handleSliderInput = (e) => {
    // Live drag — update value via onChange so the slider thumb tracks the
    // user's drag continuously. Snap-to-nearest-tick happens on release
    // (handleSliderChange below).
    fireOnChange(parseInt(e.target.value, 10));
  };

  const handleSliderRelease = (e) => {
    const ms = parseInt(e.target.value, 10);
    // Snap to nearest tick, IF a tick exists within a snap-window. Snap
    // window = ~30 minutes (covers reasonable mouse precision) BUT only if
    // there's a closer tick than 30 min away.
    if (tickMs.length === 0) {
      fireOnChange(ms);
      return;
    }
    let nearest = tickMs[0];
    let nearestDist = Math.abs(ms - nearest);
    for (const t of tickMs) {
      const d = Math.abs(ms - t);
      if (d < nearestDist) {
        nearest = t;
        nearestDist = d;
      }
    }
    // Always snap to nearest tick (no window threshold) — user explicitly
    // requested "drop on a bump" semantics.
    fireOnChange(nearest);
  };

  const onPrevEvent = () => {
    if (prevEventMs == null) return;
    fireOnChange(prevEventMs);
  };
  const onNextEvent = () => {
    if (nextEventMs == null) return;
    fireOnChange(nextEventMs);
  };

  const sliderDisabled = disabled || !dayValid;

  // Tick visualization — overlay positioned absolute on top of the slider.
  // Tick X position = ((ms - startMs) / (endMs - startMs)) * 100%.
  const tickElements = tickMs.map((ms) => {
    const pct = ((ms - startMs) / (endMs - startMs)) * 100;
    return (
      <span
        key={ms}
        data-testid="time-slider-tick"
        aria-hidden="true"
        style={{
          position: "absolute",
          left: `${pct}%`,
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "2px",
          height: "10px",
          backgroundColor: "#f5deb3",
          opacity: 0.9,
          pointerEvents: "none",
        }}
      />
    );
  });

  return (
    <div data-testid="time-slider" data-tutorial-target="time-slider" className="flex items-center gap-3 w-full">
      <button
        type="button"
        onClick={onPrevEvent}
        disabled={sliderDisabled || prevEventMs == null}
        className={`hover:opacity-75 ${
          sliderDisabled || prevEventMs == null ? "opacity-30 cursor-not-allowed" : ""
        }`}
        title={
          prevEventMs == null
            ? "No earlier events"
            : `Jump to previous event (${new Date(prevEventMs).toLocaleTimeString()})`
        }
        aria-label="Previous event"
      >
        <span className="arrow arrow-right"></span>
      </button>
      <div
        className="flex-1 relative"
        style={{ minWidth: "200px", height: "32px" }}
      >
        {tickElements}
        <input
          type="range"
          min={startMs}
          max={endMs}
          step={1000}
          value={valueMs}
          onChange={handleSliderInput}
          onMouseUp={handleSliderRelease}
          onTouchEnd={handleSliderRelease}
          onKeyUp={handleSliderRelease}
          disabled={sliderDisabled}
          data-testid="time-slider-input"
          aria-label="Time of day"
          style={{
            width: "100%",
            position: "absolute",
            top: "50%",
            left: 0,
            transform: "translateY(-50%)",
            cursor: sliderDisabled ? "not-allowed" : "pointer",
          }}
        />
      </div>
      <span
        className="text-sm font-mono tabular-nums"
        style={{ minWidth: "60px", color: "#f5deb3" }}
        data-testid="time-slider-value"
      >
        {valueValid ? formatHHMM(value) : "--:--"}
      </span>
      <button
        type="button"
        onClick={onNextEvent}
        disabled={sliderDisabled || nextEventMs == null}
        className={`hover:opacity-75 ${
          sliderDisabled || nextEventMs == null ? "opacity-30 cursor-not-allowed" : ""
        }`}
        title={
          nextEventMs == null
            ? "No later events"
            : `Jump to next event (${new Date(nextEventMs).toLocaleTimeString()})`
        }
        aria-label="Next event"
      >
        <span className="arrow"></span>
      </button>
    </div>
  );
};

function formatHHMM(date) {
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export { formatHHMM };
export default TimeSlider;
